<template>
  <div class="login-overlay">
    <div class="login-card">

      <!-- LOGIN VIEW -->
      <template v-if="view === 'login'">
        <div class="login-header">
          <h2>{{ t('chatLogin') }}</h2>
          <button class="close-btn" @click="$emit('close')">&#10005;</button>
        </div>

        <div class="form">

          <input
              v-model="username"
              type="email"
              :placeholder="t('email')"
              @keyup.enter="login"
          />

          <input
              v-model="password"
              type="password"
              :placeholder="t('password')"
              @keyup.enter="login"
          />

          <!-- CLIENT LOGIN -->
          <label v-if="!hasPrefilledText" class="client-toggle">
            <input
                type="checkbox"
                v-model="clientLogin"
            />
            {{ t('loginAsClient') }}
          </label>

          <!-- PREFILLED CHAT TEXT -->
          <div v-if="hasPrefilledText && pendingHashhashDomain" class="chattext-title">
            {{ t('messageTo') }}: {{ pendingHashhashDomain }}
          </div>
          <textarea
              v-if="hasPrefilledText"
              v-model="chatText"
              :placeholder="t('prefilledMessage')"
              class="chattext"
              rows="4"
          />

          <!-- DOMAIN INPUT -->
          <input
              v-if="!clientLogin && domainAccountList.length > 0"
              v-model="domainAccountName"
              :placeholder="t('domainAccount')"
              @keyup.enter="login"
          />

          <!-- DOMAIN LIST -->
          <div
              v-if="!clientLogin && domainAccountList.length > 0"
              class="domain-list"
          >
            <label
                v-for="(domainAccount, index) in filteredDomainAccountList"
                :key="index"
                class="radio"
            >
              <input
                  type="radio"
                  v-model="domainAccountName"
                  :value="domainAccount"
              />
              {{ domainAccount }}
            </label>
          </div>

          <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
          <div v-if="successMessage" class="success-message">{{ successMessage }}</div>

          <button :disabled="!canLogin" @click="login">
            {{ t('login') }}
          </button>

          <!-- Passwordless: request a 6-digit email code instead of using a password. -->
          <button class="code-btn" :disabled="!canRequestCode || requestingCode" @click="requestCode">
            {{ t('getEmailCode') }}
          </button>

          <label v-if="!termsAlreadyAccepted" class="terms-row">
            <input type="checkbox" v-model="termsAccepted" />
            <span>
              {{ t('termsAcceptCheckbox') }}
              <a href="#" class="terms-link" @click.prevent="termsOpen = true">{{ t('termsRead') }}</a>
            </span>
          </label>
          <a v-else href="#" class="forgot-link" @click.prevent="termsOpen = true">{{ t('termsRead') }}</a>

          <a href="#" class="forgot-link" @click.prevent="showResetView">{{ t('forgotPassword') }}</a>
          <a href="#" class="forgot-link" @click.prevent="registerOpen = true">{{ t('registerCompany') }}</a>

        </div>
      </template>

      <ChatTerms v-if="termsOpen" @close="termsOpen = false" />

      <ChatRegister v-if="registerOpen" @close="registerOpen = false" />

      <EmailCodeDialog
          v-if="emailCodeOpen"
          :service="service"
          :email="username"
          @success="onCodeSuccess"
          @cancel="emailCodeOpen = false"
      />

      <!-- RESET PASSWORD VIEW -->
      <template v-if="view === 'reset'">
        <div class="login-header">
          <h2>{{ t('resetPasswordTitle') }}</h2>
          <button class="close-btn" @click="$emit('close')">&#10005;</button>
        </div>

        <div class="form">

          <input
              v-model="resetEmail"
              type="email"
              :placeholder="t('email')"
          />

          <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

          <button :disabled="!canResetPassword" @click="resetPassword">
            {{ t('requestPassword') }}
          </button>

          <a href="#" class="forgot-link" @click.prevent="showLoginView">{{ t('backToLogin') }}</a>

        </div>
      </template>

      <div v-if="commitDate" class="commit-info">{{ commitDate }}</div>

    </div>
  </div>
</template>

<script>
import axios from "axios"
import { useI18n } from "../i18n.js"
import ChatTerms from "./ChatTerms.vue"
import ChatRegister from "./ChatRegister.vue"
import EmailCodeDialog from "./EmailCodeDialog.vue"
import ChatApiService from "../services/ChatApiService.js"
import { isTermsAccepted, markTermsAccepted } from "../terms.js"

axios.defaults.withCredentials = true

const TT_URL = "";

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

function validEmail(email) {
  return !!email && EMAIL_REGEX.test(email);
}

function decodeBase64Utf8(b64) {
  try {
    const binary = atob(b64);
    const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  } catch (e) {
    return '';
  }
}

function getRawParam(name) {
  const m = window.location.href.match(new RegExp(`[?&]${name}=([^&]+)`));
  return m ? decodeURIComponent(m[1]) : '';
}

function readUrlParams() {
  const rawChattext = getRawParam('chattext');
  const hashhashDomain = getRawParam('hashhashDomain');
  const clientAccountName = getRawParam('clientAccountName');
  const text = rawChattext ? decodeBase64Utf8(rawChattext) : '';
  return { text, hashhashDomain, clientAccountName };
}

function getCookie(name) {
  const parts = document.cookie ? document.cookie.split(';') : [];
  for (const p of parts) {
    const [rawKey, ...rest] = p.split('=');
    if (rawKey && rawKey.trim() === name) {
      try { return decodeURIComponent(rest.join('=')); }
      catch (e) { return rest.join('='); }
    }
  }
  return '';
}

function initialUsername() {
  return getRawParam('Email') || getRawParam('email') || getCookie('lastusername') || '';
}

function initialDomainAccountName() {
  const account = getCookie('lastaccountname');
  const domain = getCookie('lastdomainname');
  return account && domain ? `${account}##${domain}` : '';
}

export default {
  name: "ChatLogin",

  components: { ChatTerms, ChatRegister, EmailCodeDialog },

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    const urlParams = readUrlParams();
    return {
      // Passwordless login (email code). Domain is irrelevant here — the endpoints use
      // the fixed `default` (client) path.
      service: new ChatApiService('default', {}),
      emailCodeOpen: false,
      requestingCode: false,
      commitDate: typeof __LAST_COMMIT_DATE__ !== "undefined" ? __LAST_COMMIT_DATE__ : "",
      view: "login",
      username: initialUsername(),
      password: "",
      domainAccountName: "",
      domainAccountList: [],
      // Always start as "login as client" — domainAccountName stays out of the request
      // unless the user explicitly unchecks the box.
      clientLogin: true,
      // Remembered hint from cookies, only applied when the user switches to domain login.
      rememberedDomainAccount: initialDomainAccountName(),
      loadingDomains: false,
      errorMessage: "",
      successMessage: "",
      resetEmail: "",
      chatText: urlParams.text,
      hasPrefilledText: !!urlParams.text,
      pendingHashhashDomain: urlParams.hashhashDomain,
      pendingClientAccountName: urlParams.clientAccountName,
      termsOpen: false,
      termsAccepted: false,
      registerOpen: false
    };
  },

  computed: {

    termsAlreadyAccepted() {
      return isTermsAccepted(this.username);
    },

    canLogin() {
      const termsOk = this.termsAlreadyAccepted || this.termsAccepted;
      return validEmail(this.username) && this.password.length > 0 && termsOk;
    },

    canResetPassword() {
      return validEmail(this.resetEmail);
    },

    canRequestCode() {
      const termsOk = this.termsAlreadyAccepted || this.termsAccepted;
      return validEmail(this.username) && termsOk;
    },

    filteredDomainAccountList() {
      const q = (this.domainAccountName || '').toLowerCase().trim();
      if (!q) return this.domainAccountList;
      return this.domainAccountList.filter(d => d.toLowerCase().includes(q));
    }

  },

  watch: {

    username(val) {
      if (!this.clientLogin) {
        this.loadDomains(val);
      }
    },

    clientLogin(val) {
      if (!val) {
        // Switching to domain login — pre-fill from cookie hint if still empty.
        if (!this.domainAccountName && this.rememberedDomainAccount) {
          this.domainAccountName = this.rememberedDomainAccount;
        }
        this.loadDomains(this.username);
      } else {
        // Switching back to client login — drop any picked domain account
        // so it cannot be sent with the login request by accident.
        this.domainAccountName = "";
      }
    }

  },

  methods: {

    login() {

      if (!this.canLogin) return;

      let domain = "default";

      if (!this.clientLogin && this.domainAccountName) {
        let parts = this.domainAccountName.split("##");
        domain = parts.length > 1 ? parts[1] : parts[0];
      }

      this.errorMessage = "";
      this.successMessage = "";

      axios
      .post(TT_URL + "/tt/api/tibtix.com/login", {
        username: this.username,
        password: this.password,
        domainAccountName: this.clientLogin
            ? null
            : this.domainAccountName
      })
      .then(response => {

        const data = response.data;

        if (!data.loggedIn) {
          this.errorMessage = this.formatLoginError(data);
          return;
        }

        const roles = Array.isArray(data.roles) ? data.roles : [];
        // Single source of truth for the client/domain distinction:
        // the user is a client iff they do NOT have the domain-user role.
        const clientLogin = !roles.includes('domain-user');

        markTermsAccepted(this.username);

        this.$emit("logged-in", {
          username: this.username,
          password: this.password,
          domain: domain,
          domainAccountName: this.domainAccountName,
          clientLogin,
          roles,
          loggedInWithTempPassword: !!data.loggedInWithTempPassword,
          pendingMessage: this.hasPrefilledText ? this.chatText : "",
          pendingHashhashDomain: this.hasPrefilledText ? this.pendingHashhashDomain : "",
          pendingClientAccountName: this.hasPrefilledText ? this.pendingClientAccountName : ""
        });

      })
      .catch(error => {
        const data = error.response?.data;
        this.errorMessage = data ? this.formatLoginError(data) : this.t('loginFailed');
      });
    },

    async requestCode() {
      if (!this.canRequestCode || this.requestingCode) return;
      this.errorMessage = "";
      this.requestingCode = true;
      try {
        await this.service.requestEmailCode(this.username);
      } catch (e) { /* neutral */ } finally {
        this.requestingCode = false;
      }
      this.emailCodeOpen = true;
    },

    // EmailCodeDialog succeeded -> build a session payload like the password login does,
    // so App can claim a pending anonymous chat and bring up the chat.
    onCodeSuccess(data) {
      this.emailCodeOpen = false;
      const roles = Array.isArray(data?.roles) ? data.roles : [];
      // Code login always yields a CLIENT session (path segment "default").
      const clientLogin = !roles.includes('domain-user');
      markTermsAccepted(this.username);
      this.$emit("logged-in", {
        username: this.username,
        password: "",
        domain: "default",
        domainAccountName: "",
        clientLogin,
        roles,
        loggedInWithTempPassword: false,
        pendingMessage: this.hasPrefilledText ? this.chatText : "",
        pendingHashhashDomain: this.hasPrefilledText ? this.pendingHashhashDomain : "",
        pendingClientAccountName: this.hasPrefilledText ? this.pendingClientAccountName : ""
      });
    },

    formatLoginError(data) {
      const code = data?.errorCode;
      if (code === 'USER_LOGIN_LOCKED') {
        const until = data.lockedUntil ? new Date(data.lockedUntil).toLocaleString() : '';
        return this.t('errorLoginLocked').replace('{until}', until);
      }
      if (code === 'USER_LOGIN_FAILED') return this.t('errorLoginFailed');
      if (code === 'USER_NOT_LOGGED_IN') return this.t('errorNotLoggedIn');
      return data?.message || this.t('loginFailed');
    },

    showResetView() {
      this.resetEmail = this.username;
      this.errorMessage = "";
      this.successMessage = "";
      this.view = "reset";
    },

    showLoginView() {
      this.errorMessage = "";
      this.successMessage = "";
      this.view = "login";
    },

    resetPassword() {
      if (!this.canResetPassword) return;

      this.errorMessage = "";

      axios
      .post(TT_URL + "/tt/view/forgotPasswordJson", {
        email: this.resetEmail
      })
      .then(response => {
        if (response.data.success) {
          this.successMessage = response.data.message || this.t('resetPasswordSent').replace('{email}', this.resetEmail);
          this.username = this.resetEmail;
          this.view = "login";
        } else {
          this.errorMessage = response.data.message || this.t('resetPasswordFailed');
        }
      })
      .catch(error => {
        this.errorMessage = error.response?.data?.message || this.t('resetPasswordFailed');
      });
    },

    loadDomains(email) {

      if (!validEmail(email)) return;
      if (this.loadingDomains) return;

      this.loadingDomains = true;

      axios
      .post(TT_URL + "/tt/api/tib/getDomainAccountsByEmail", {
        email: email
      })
      .then(response => {
        this.domainAccountList = response.data;
      })
      .finally(() => {
        this.loadingDomains = false;
      });
    }

  }
};
</script>

<style scoped>
.login-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.login-header h2 {
  margin: 0;
}

.close-btn {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}

.close-btn:hover {
  color: #000;
}

.login-card {
  width: min(420px, calc(100vw - 32px));
  background: white;
  padding: 24px;
  border-radius: 12px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

input {
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-family: inherit;
  font-size: 16px;
}

textarea.chattext {
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-family: inherit;
  font-size: 16px;
  resize: vertical;
  line-height: 1.4;
}

.chattext-title {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: -6px;
}

button {
  padding: 12px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;
}

button:disabled {
  background: #c7c9d1;
  cursor: not-allowed;
}

.code-btn {
  background: #f0f2f5;
  color: #1a1a2e;
}
.code-btn:hover:not(:disabled) { background: #e4e8ee; }
.code-btn:disabled { background: #e9eaef; color: #9aa0ac; }

.client-toggle {
  display: flex;
  gap: 8px;
  align-items: center;
}

.terms-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 13px;
  color: #374151;
  line-height: 1.4;
}

.terms-row input[type="checkbox"] {
  margin-top: 2px;
  flex-shrink: 0;
}

.terms-link {
  color: #2563eb;
  text-decoration: underline;
  margin-left: 4px;
}

.domain-list {
  max-height: 200px;
  overflow-y: auto;
  background: #f5f5f5;
  padding: 8px;
  border-radius: 6px;
}

.radio {
  display: block;
  margin: 4px 0;
}

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.success-message {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.forgot-link {
  text-align: center;
  font-size: 13px;
  color: #6b7280;
  text-decoration: none;
}

.forgot-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.commit-info {
  margin-top: 16px;
  text-align: center;
  font-size: 10px;
  color: #b0b8c9;
  font-variant-numeric: tabular-nums;
}
</style>
